<template>
  <nav
    class="main-navigation"
    data-test="main-navigation"
    :class="{
      'main-navigation--expanded': openSectionKey !== null,
      'main-navigation--impersonation-active': authStore.isImpersonationActive,
    }"
    @wheel="preventScrollWhenExpanded"
  >
    <ul class="main-navigation__list">
      <li class="main-navigation__item">
        <router-link
          :to="{ name: RouteNames.DASHBOARD }"
          class="main-navigation__top-link"
          :class="{
            'main-navigation__top-link--active':
              route.name === RouteNames.DASHBOARD,
          }"
          data-test="navi-link-dashboard"
          @click="closeSection"
        >
          <PhHouse class="main-navigation__top-icon" weight="bold" :size="16" />
          <span>{{ $t("navigation.home") }}</span>
        </router-link>
      </li>

      <li
        v-for="section in visibleNavigationSections"
        :key="section.key"
        class="main-navigation__item main-navigation__item--has-submenu"
        :class="{
          'main-navigation__item--active': isSectionActive(section),
          'main-navigation__item--open': openSectionKey === section.key,
        }"
        :data-test="section.dataTest"
        @mouseenter="openSection(section.key)"
        @mouseleave="scheduleCloseSection"
      >
        <button class="main-navigation__top-button" type="button">
          <component
            :is="section.icon"
            class="main-navigation__top-icon"
            weight="bold"
            :size="16"
          />
          <span>{{ section.title }}</span>
          <PhCaretDown
            class="main-navigation__chevron"
            weight="bold"
            :size="12"
          />
        </button>

        <div class="main-navigation__submenu">
          <div class="main-navigation__submenu-inner">
            <router-link
              v-for="item in section.items"
              :key="item.key"
              :to="item.to"
              class="main-navigation__submenu-card"
              :class="{
                'main-navigation__submenu-card--active':
                  isNavigationTargetActive(item.to),
              }"
              :data-test="item.dataTest"
              @click="closeSection"
            >
              <span class="main-navigation__submenu-icon-wrapper">
                <component
                  :is="item.icon"
                  class="main-navigation__submenu-icon"
                  weight="bold"
                  :size="22"
                />
              </span>

              <span class="main-navigation__submenu-content">
                <span class="main-navigation__submenu-title">
                  {{ item.title }}
                </span>
                <span class="main-navigation__submenu-description">
                  {{ item.description }}
                </span>
              </span>
            </router-link>
          </div>
        </div>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, type Component } from "vue";
import { RouteNames } from "@/router";
import { usePermissions } from "@/common/permission";
import { useGeneralStore } from "@/stores/general";
import { useI18n } from "vue-i18n";
import Config from "@/common/config";
import {
  PhAddressBook,
  PhArrowCircleDown,
  PhArrowCircleUp,
  PhCalculator,
  PhCaretDown,
  PhClipboardText,
  PhClockClockwise,
  PhClockCounterClockwise,
  PhCube,
  PhFileText,
  PhGear,
  PhHandCoins,
  PhHouse,
  PhKey,
  PhListMagnifyingGlass,
  PhMapPinLine,
  PhSliders,
  PhTranslate,
  PhTruck,
  PhWarehouse,
  PhWrench,
} from "@phosphor-icons/vue";

import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const { t } = useI18n();

const { permissionPresent, permissionsReady, hasPermissionUsageQueryAccess } =
  usePermissions();

const generalStore = useGeneralStore();

const fullscreen = computed(() => {
  return generalStore.fullscreen;
});

const QUOTE_TOOL_COMPANY_PERMISSIONS = ["TUS010010", "TUS010011"];

const quoteToolCompanyPermissionPresent = computed(
  () =>
    authStore.currentUser?.compassInformation?.companyPermissions?.some(
      ({ permission }) => QUOTE_TOOL_COMPANY_PERMISSIONS.includes(permission),
    ) ?? false,
);

type NavigationTarget =
  | {
      name: RouteNames;
    }
  | string;

type NavigationItem = {
  key: string;
  title: string;
  description: string;
  to: NavigationTarget;
  dataTest: string;
  icon: Component;
};

type NavigationSection = {
  key: string;
  title: string;
  icon: Component;
  dataTest: string;
  items: NavigationItem[];
};

const openSectionKey = ref<string | null>(null);

let closeTimeout: ReturnType<typeof setTimeout> | undefined;
let switchTimeout: ReturnType<typeof setTimeout> | undefined;

const clearCloseTimeout = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = undefined;
  }
};

const clearSwitchTimeout = () => {
  if (switchTimeout) {
    clearTimeout(switchTimeout);
    switchTimeout = undefined;
  }
};

const openSection = (sectionKey: string) => {
  clearCloseTimeout();
  clearSwitchTimeout();

  if (!openSectionKey.value) {
    openSectionKey.value = sectionKey;
    return;
  }

  if (openSectionKey.value === sectionKey) {
    return;
  }

  switchTimeout = setTimeout(() => {
    openSectionKey.value = sectionKey;
    switchTimeout = undefined;
  }, 100);
};

const closeSection = () => {
  clearCloseTimeout();
  clearSwitchTimeout();
  openSectionKey.value = null;
};

const scheduleCloseSection = () => {
  clearCloseTimeout();

  closeTimeout = setTimeout(() => {
    clearSwitchTimeout();
    openSectionKey.value = null;
  }, 150);
};

onBeforeUnmount(() => {
  clearCloseTimeout();
  clearSwitchTimeout();
});

const isNavigationTargetActive = (to: NavigationTarget) => {
  const resolvedTarget = router.resolve(to);

  if (typeof to !== "string") {
    return resolvedTarget.name === route.name;
  }

  return resolvedTarget.path === route.path;
};

const isSectionActive = (section: NavigationSection) => {
  return section.items.some((item) => isNavigationTargetActive(item.to));
};

const visibleNavigationSections = computed<NavigationSection[]>(() => {
  const reports: NavigationItem[] = [];
  const inventoryManagementPages: NavigationItem[] = [];
  const servicesPages: NavigationItem[] = [];
  const toolPages: NavigationItem[] = [];
  const mapPages: NavigationItem[] = [];

  fullscreen.value;

  if (!permissionsReady.value) {
    return [];
  }

  if (permissionPresent("REPORT_ITR")) {
    reports.push({
      key: "itr",
      title: t("reports.itr"),
      description: t("navigationHelp.reports.itr"),
      to: { name: RouteNames.REPORT_ITR },
      dataTest: "navi-link-report-itr",
      icon: PhListMagnifyingGlass,
    });
  }

  if (permissionPresent("REPORT_OOR")) {
    reports.push({
      key: "oor",
      title: t("reports.oor"),
      description: t("navigationHelp.reports.oor"),
      to: { name: RouteNames.REPORT_OOR },
      dataTest: "navi-link-report-oor",
      icon: PhClipboardText,
    });
  }

  if (permissionPresent("REPORT_WIR")) {
    reports.push({
      key: "wir",
      title: t("reports.wir"),
      description: t("navigationHelp.reports.wir"),
      to: { name: RouteNames.REPORT_WIR },
      dataTest: "navi-link-report-wir",
      icon: PhWarehouse,
    });
  }

  if (permissionPresent("REPORT_WIR_IM_VIEW")) {
    inventoryManagementPages.push(
      {
        key: "deliveryIncoming",
        title: t("inventoryManagementPages.deliveryIncoming"),
        description: t("navigationHelp.inventoryManagement.deliveryIncoming"),
        to: "/ims/deliveryIncoming",
        dataTest: "navi-link-ims-deliveryIncoming",
        icon: PhArrowCircleDown,
      },
      {
        key: "deliveryOutgoing",
        title: t("inventoryManagementPages.deliveryOutgoing"),
        description: t("navigationHelp.inventoryManagement.deliveryOutgoing"),
        to: "/ims/deliveryOutgoing",
        dataTest: "navi-link-ims-deliveryOutgoing",
        icon: PhArrowCircleUp,
      },
      {
        key: "deliveryReceived",
        title: t("inventoryManagementPages.deliveryReceived"),
        description: t("navigationHelp.inventoryManagement.deliveryReceived"),
        to: "/ims/deliveryReceived",
        dataTest: "navi-link-ims-deliveryReceived",
        icon: PhClockCounterClockwise,
      },
      {
        key: "deliverySent",
        title: t("inventoryManagementPages.deliverySent"),
        description: t("navigationHelp.inventoryManagement.deliverySent"),
        to: "/ims/deliverySent",
        dataTest: "navi-link-ims-deliverySent",
        icon: PhClockClockwise,
      },
      {
        key: "inventoryIms",
        title: t("inventoryManagementPages.inventoryIms"),
        description: t("navigationHelp.inventoryManagement.inventoryIms"),
        to: "/ims/inventoryIms",
        dataTest: "navi-link-ims-inventoryIms",
        icon: PhSliders,
      },
    );
  }

  if (Config.isCompass) {
    if (quoteToolCompanyPermissionPresent.value) {
      toolPages.push({
        key: "quoteTool",
        title: t("quotationTool.quoteTool"),
        description: t("navigationHelp.tools.quoteTool"),
        to: { name: RouteNames.QUOTE_TOOL },
        dataTest: "navi-link-tools-quoteTool",
        icon: PhFileText,
      });
    }

    servicesPages.push(
      {
        key: "address",
        title: t("services.address"),
        description: t("navigationHelp.services.address"),
        to: { name: RouteNames.ADDRESS },
        dataTest: "navi-link-services-address",
        icon: PhAddressBook,
      },
      {
        key: "debtorCreditor",
        title: t("services.debtorCreditor"),
        description: t("navigationHelp.services.debtorCreditor"),
        to: { name: RouteNames.CUSTOMER_ADDRESS },
        dataTest: "navi-link-services-debtorCreditor",
        icon: PhHandCoins,
      },
    );

    if (hasPermissionUsageQueryAccess.value) {
      servicesPages.push({
        key: "permissionUsageQuery",
        title: t("services.permissionUsageQuery"),
        description: t("navigationHelp.services.permissionUsageQuery"),
        to: { name: RouteNames.PERMISSION_USAGE_QUERY },
        dataTest: "navi-link-services-permissionUsageQuery",
        icon: PhKey,
      });
    }
  }

  if (Config.isCompass) {
    toolPages.push({
      key: "translation",
      title: t("quotationTool.translation"),
      description: t("navigationHelp.tools.translation"),
      to: { name: RouteNames.TRANSLATION_TOOL },
      dataTest: "navi-link-tools-translation",
      icon: PhTranslate,
    });
  } else if (permissionPresent("QUOTE_VIEW")) {
    toolPages.push({
      key: "newOffer",
      title: t("quotationTool.newOffer"),
      description: t("navigationHelp.tools.newOffer"),
      to: { name: RouteNames.NEW_OFFER },
      dataTest: "navi-link-tools-newOffer",
      icon: PhClipboardText,
    });
  }

  toolPages.push({
    key: "calculator",
    title: t("quotationTool.calculator"),
    description: t("navigationHelp.tools.calculator"),
    to: { name: RouteNames.CHARGE_WEIGHT_CALCULATOR },
    dataTest: "navi-link-tools-calculator",
    icon: PhCalculator,
  });

  if (Config.isCompass) {
    mapPages.push({
      key: "truckDisposition",
      title: t("location.truckDisposition"),
      description: t("navigationHelp.maps.truckDisposition"),
      to: { name: RouteNames.TRUCK_DISPOSITION_MAP },
      dataTest: "navi-link-maps-truckDisposition",
      icon: PhTruck,
    });
  }

  return [
    {
      key: "reports",
      title: t("navigation.reports"),
      icon: PhFileText,
      dataTest: "navi-section-reports",
      items: reports,
    },
    {
      key: "inventoryManagement",
      title: t("navigation.inventoryManagement"),
      icon: PhCube,
      dataTest: "navi-section-ims",
      items: inventoryManagementPages,
    },
    {
      key: "services",
      title: t("services.title"),
      icon: PhGear,
      dataTest: "navi-section-services",
      items: servicesPages,
    },
    {
      key: "tools",
      title: t("navigation.tools"),
      icon: PhWrench,
      dataTest: "navi-section-tools",
      items: toolPages,
    },
    {
      key: "maps",
      title: t("navigation.maps"),
      icon: PhMapPinLine,
      dataTest: "navi-section-maps",
      items: mapPages,
    },
  ].filter((section) => section.items.length > 0);
});

const preventScrollWhenExpanded = (event: WheelEvent) => {
  if (openSectionKey.value !== null) {
    event.preventDefault();
  }
};
</script>

<style scoped lang="scss">
.main-navigation {
  height: 100%;
  position: relative;
  background: white;

  --navigation-dropdown-top: #{$layout-header-height};
}

.main-navigation--impersonation-active {
  --navigation-dropdown-top: calc(#{$layout-header-height} + 2rem);
}

.main-navigation--expanded::after {
  content: "";
  position: fixed;
  top: var(--navigation-dropdown-top);
  left: 0;
  width: 100vw;
  height: 0.8rem;
  pointer-events: none;
  z-index: 9999;

  background: linear-gradient(
    to bottom,
    rgb(0 0 0 / 10%) 0%,
    rgb(0 0 0 / 7%) 10%,
    rgb(0 0 0 / 5%) 25%,
    rgb(0 0 0 / 3.5%) 45%,
    rgb(0 0 0 / 2%) 65%,
    rgb(0 0 0 / 1%) 80%,
    rgb(0 0 0 / 0.7%) 92%,
    rgb(0 0 0 / 0%) 100%
  );
}

.main-navigation__list {
  display: flex;
  height: 100%;
  list-style: none;
  margin: 0;
  padding: 0;
}

.main-navigation__item {
  display: flex;
}

.main-navigation__top-link,
.main-navigation__top-button {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  height: 100%;
  padding: 0 0.9rem;
  border: 0;
  border-left: 0.1rem solid transparent;
  border-right: 0.1rem solid transparent;
  background: white;
  color: $color-blue-dark;
  font: inherit;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
}

.main-navigation__top-link:hover,
.main-navigation__top-button:hover,
.main-navigation__item--open .main-navigation__top-button {
  background: #f7f8fb;
  border-left-color: #e5e8ef;
  border-right-color: #e5e8ef;
}

.main-navigation__top-icon {
  flex: 0 0 auto;
  color: currentColor;
}

.main-navigation__chevron {
  flex: 0 0 auto;
  color: currentColor;
  transition: transform 0.15s ease;
}

.main-navigation__item--open .main-navigation__chevron {
  transform: rotate(180deg);
}

.main-navigation__submenu {
  position: fixed;
  top: var(--navigation-dropdown-top);
  left: 0;
  z-index: 20;
  display: none;
  width: 100vw;
  background: white;
  border-top: 0;
  border-bottom: 0.1rem solid #e5e8ef;
}

.main-navigation__item--open .main-navigation__submenu {
  display: block;
}

.main-navigation__submenu-inner {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.6rem;
  max-width: $layout-max-content-width;
  margin: 0 auto;
  padding: 1.8rem 2rem;
}

.main-navigation__submenu-card {
  display: flex;
  gap: 1.4rem;
  min-height: 7.6rem;
  padding: 1.6rem;
  background: white;
  border: 0.1rem solid #e1e5ec;
  box-shadow: 0 0.2rem 0.8rem rgb(0 0 0 / 6%);
  color: $color-blue-dark;
  text-decoration: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

.main-navigation__submenu-card:hover {
  border-color: $color-etBlue;
  box-shadow: 0 0.6rem 1.6rem rgb(0 0 0 / 12%);
  transform: translateY(-0.1rem);
}

.main-navigation__submenu-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 4.2rem;
  width: 4.2rem;
  height: 4.2rem;
  background: $color-etBlue;
}

.main-navigation__submenu-icon {
  color: white;
}

.main-navigation__submenu-content {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.main-navigation__submenu-title {
  color: $color-blue-dark;
  font-weight: 700;
}

.main-navigation__submenu-description {
  font-size: $font-size-base;
}

@media screen and (max-width: $breakpoint-medium) {
  .main-navigation {
    overflow-x: auto;
  }

  .main-navigation__top-link,
  .main-navigation__top-button {
    padding: 0 1.2rem;
  }

  .main-navigation__submenu {
    top: $layout-header-height;
  }

  .main-navigation__submenu-inner {
    grid-template-columns: 1fr;
  }
}

.main-navigation__item--active .main-navigation__top-button {
  color: $color-etBlue;
}

.main-navigation__submenu-card--active {
  border-color: $color-etBlue;
}

.main-navigation__top-link--active {
  color: $color-etBlue;
}
</style>
